import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import contactLogo from "../../image/contact/contact-logo.png";

// Logo Container Component
function LogoContainer() {
  return (
    <div
      className="box-border content-stretch flex flex-row gap-2 h-6 items-end justify-start opacity-80 p-0 relative shrink-0"
      data-name="Logo Container"
    >
      <div
        className="aspect-[1009/176] bg-center bg-cover bg-no-repeat h-full opacity-80 shrink-0"
        data-name="벅스_로고 단색 (1)"
        style={{ backgroundImage: `url('${contactLogo}')` }}
      />
      <div className="flex flex-col font-paperlogy font-bold h-full justify-end leading-[0] not-italic relative shrink-0 text-[16px] text-[rgba(0,0,0,0.9)] text-left tracking-[-0.32px] w-[115px]">
        <p className="block leading-[normal] font-bold">디지털 키즈카페</p>
      </div>
    </div>
  );
}

// Hover Popup Button Component
function HoverPopupButton({ isVisible, buttonText, onClick }: { 
  isVisible: boolean; 
  buttonText: string;
  onClick: () => void;
}) {
  return (
    <>
      {/* Add global styles for floating animation */}
      <style>{`
        @keyframes floatUpDown {
          0%, 100% { 
            transform: translateY(0px) translateX(-50%); 
          }
          50% { 
            transform: translateY(-3px) translateX(-50%); 
          }
        }
        
        .popup-floating {
          animation: floatUpDown 3s ease-in-out infinite;
        }
      `}</style>
      
      <button
        onClick={onClick}
        className={`absolute bg-[#5c5c5c] hover:bg-[#4a4a4a] box-border content-stretch flex flex-row gap-2.5 h-12 items-center justify-center px-5 py-2.5 rounded-[100px] top-[calc(100%+10px)] z-50 transition-all duration-300 ease-out cursor-pointer ${
          isVisible 
            ? 'opacity-100 translate-y-0 visible popup-floating' 
            : 'opacity-0 translate-y-[-10px] invisible'
        }`}
        style={{ 
          left: '50%', 
          transform: 'translateX(-50%)',
          minWidth: '110px'
        }}
        data-name="Popup Button Container"
      >
        <div className="font-paperlogy font-medium leading-[0] not-italic relative shrink-0 text-[#ffffff] text-[14px] text-center text-nowrap tracking-[-0.28px]">
          <p className="block leading-[normal] whitespace-pre">{buttonText}</p>
        </div>
      </button>
    </>
  );
}

// Navigation Container Component
function NavigationContainer({ isMobile = false, isHomePage = true, onMenuClick, onFacilityClick, onNoticeClick, onBusinessClick, onHomeClick }: { 
  isMobile?: boolean; 
  isHomePage?: boolean;
  onMenuClick?: () => void;
  onFacilityClick?: () => void;
  onNoticeClick?: () => void;
  onBusinessClick?: () => void;
  onHomeClick?: () => void;
}) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  
  // 모든 페이지에서 동일한 네비게이션 아이템 표시 (홈페이지 기준)
  const navItems = [
    { 
      label: '홈', 
      key: '홈', 
      hasPopup: false,
      popupText: ''
    },
    { 
      label: '시설', 
      key: '시설', 
      hasPopup: true,
      popupText: '시설 더보기'
    },
    { 
      label: '메뉴', 
      key: '메뉴', 
      hasPopup: true,
      popupText: '메뉴 더보기'
    },
    { 
      label: '이용안내', 
      key: '이용안내', 
      hasPopup: false,
      popupText: ''
    },
    { 
      label: '공지사항', 
      key: '공지사항', 
      hasPopup: true,
      popupText: '공지 더보기'
    },
    { 
      label: '창업안내', 
      key: '창업안내', 
      hasPopup: true,
      popupText: '창업 더보기'
    },
    { 
      label: 'contact', 
      key: 'contact', 
      hasPopup: false,
      popupText: ''
    }
  ];
  
  const handleItemClick = (item: string) => {
    // 홈 메뉴는 항상 최상단으로 스크롤
    if (item === '홈') {
      // 홈페이지에 있는 경우 최상단으로 스크롤
      if (isHomePage) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // 다른 페이지에서는 홈페이지로 이동 후 최상단으로
        if (onHomeClick) {
          onHomeClick();
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }
      }
      return;
    }
    
    // 섹션 맵 정의
    const sectionMap: { [key: string]: string } = {
      '시설': 'facility-section',
      '메뉴': 'menu-section',
      '이용안내': 'usage-section',
      '공지사항': 'notice-section',
      '창업안내': 'business-section',
      'contact': 'contact-section'
    };
    
    const targetId = sectionMap[item];
    const element = targetId ? document.getElementById(targetId) : null;
    
    if (element) {
      // 홈페이지에서 섹션이 존재하면 스크롤 이동
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // 서브페이지에서는 홈페이지로 이동 후 해당 섹션으로 스크롤
      if (onHomeClick) {
        onHomeClick();
        // 홈페이지 로드 후 섹션으로 스크롤하기 위해 지연 실행
        setTimeout(() => {
          const homeElement = document.getElementById(targetId);
          if (homeElement) {
            homeElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  };

  const handlePopupClick = (item: string) => {
    // 팝업 버튼 클릭 시 해당 페이지로 이동
    if (item === '메뉴' && onMenuClick) {
      onMenuClick();
    } else if (item === '시설' && onFacilityClick) {
      onFacilityClick();
    } else if (item === '공지사항' && onNoticeClick) {
      onNoticeClick();
    } else if (item === '창업안내' && onBusinessClick) {
      onBusinessClick();
    }
  };
  
  if (isMobile) {
    return (
      <div className="flex flex-col gap-4 p-4">
        {navItems.map((item, index) => (
          <div key={index} className="relative">
            <button 
              onClick={() => handleItemClick(item.key)}
              className="adjustLetterSpacing block leading-[normal] text-nowrap whitespace-pre font-paperlogy font-medium text-[16px] text-[rgba(0,0,0,0.9)] tracking-[-0.32px] text-left"
            >
              {item.label}
            </button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="hidden lg:flex box-border content-stretch flex-row font-paperlogy font-medium gap-10 items-center justify-start leading-[0] not-italic p-0 relative shrink-0 text-[16px] text-left text-[rgba(0,0,0,0.9)] text-nowrap tracking-[-0.32px]"
      data-name="Navigation Container"
    >
      {navItems.map((item, index) => (
        <div 
          key={index} 
          className="relative shrink-0"
          onMouseEnter={() => setHoveredItem(item.key)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <button 
            onClick={() => handleItemClick(item.key)}
            className="adjustLetterSpacing block leading-[normal] text-nowrap whitespace-pre transition-all duration-200 relative px-3 py-3 rounded-lg hover:font-semibold hover:bg-black/5 hover:shadow-sm"
            style={{ minWidth: 'max-content' }}
          >
            {item.label}
          </button>
          
          {/* Hover Popup Button for items that have popup */}
          {item.hasPopup && (
            <HoverPopupButton 
              isVisible={hoveredItem === item.key}
              buttonText={item.popupText}
              onClick={() => handlePopupClick(item.key)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// Mobile Header Component
function MobileHeader({ scrolled, isHomePage = true, onMenuClick, onFacilityClick, onNoticeClick, onBusinessClick, onHomeClick }: { 
  scrolled: boolean;
  isHomePage?: boolean;
  onMenuClick?: () => void;
  onFacilityClick?: () => void;
  onNoticeClick?: () => void;
  onBusinessClick?: () => void;
  onHomeClick?: () => void;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const headerBgClass = scrolled 
    ? "bg-[rgba(255,255,255,0.5)] backdrop-blur-md border-b border-white/20" 
    : "bg-[rgba(255,255,255,0.2)] backdrop-blur-sm";

  return (
    <div className="lg:hidden">
      <div className={`absolute h-[60px] left-0 top-0 w-full z-50 transition-all duration-300 ${headerBgClass}`}>
        <div className="absolute box-border content-stretch flex flex-row h-[60px] items-center justify-between left-0 right-0 px-4 top-0">
          <LogoContainer />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-[rgba(0,0,0,0.9)] transition-all duration-200 z-50 p-2 rounded-lg hover:bg-gray-200/30 hover:shadow-sm"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {isMobileMenuOpen && (
          <div className="absolute top-[60px] left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-40">
            <NavigationContainer 
              isMobile={true} 
              isHomePage={isHomePage}
              onMenuClick={onMenuClick}
              onFacilityClick={onFacilityClick}
              onNoticeClick={onNoticeClick}
              onBusinessClick={onBusinessClick}
              onHomeClick={onHomeClick}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Desktop Header Container
function HeaderContainer({ isHomePage = true, onMenuClick, onFacilityClick, onNoticeClick, onBusinessClick, onHomeClick }: { 
  isHomePage?: boolean;
  onMenuClick?: () => void;
  onFacilityClick?: () => void;
  onNoticeClick?: () => void;
  onBusinessClick?: () => void;
  onHomeClick?: () => void;
}) {
  return (
    <div
      className="absolute box-border content-stretch flex flex-row h-[60px] items-center justify-between left-1/2 px-4 top-0 translate-x-[-50%] w-[min(1280px,_calc(100vw_-_40px))]"
      data-name="Header Container"
    >
      <LogoContainer />
      <NavigationContainer 
        isHomePage={isHomePage}
        onMenuClick={onMenuClick}
        onFacilityClick={onFacilityClick}
        onNoticeClick={onNoticeClick}
        onBusinessClick={onBusinessClick}
        onHomeClick={onHomeClick}
      />
    </div>
  );
}

// Main Header Component
export function Header({ isHomePage = true, onMenuClick, onFacilityClick, onNoticeClick, onBusinessClick, onHomeClick }: { 
  isHomePage?: boolean;
  onMenuClick?: () => void;
  onFacilityClick?: () => void;
  onNoticeClick?: () => void;
  onBusinessClick?: () => void;
  onHomeClick?: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const shouldBeScrolled = scrollTop > 50; // 50px 이상 스크롤 시 배경 변경
      
      if (shouldBeScrolled !== scrolled) {
        setScrolled(shouldBeScrolled);
      }
    };

    // 초기 스크롤 위치 확인
    handleScroll();
    
    // 스크롤 이벤트 리스너 추가
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const headerBgClass = scrolled 
    ? "bg-[rgba(255,255,255,0.5)] backdrop-blur-md border-b border-white/20 shadow-sm" 
    : "bg-[rgba(255,255,255,0.2)] backdrop-blur-sm";

  return (
    <>
      <div className="hidden lg:block">
        <div
          className={`fixed h-[60px] left-0 top-0 w-full z-50 transition-all duration-300 ${headerBgClass}`}
          data-name="Header"
        >
          <HeaderContainer 
            isHomePage={isHomePage}
            onMenuClick={onMenuClick}
            onFacilityClick={onFacilityClick}
            onNoticeClick={onNoticeClick}
            onBusinessClick={onBusinessClick}
            onHomeClick={onHomeClick}
          />
        </div>
      </div>
      <MobileHeader 
        scrolled={scrolled}
        isHomePage={isHomePage}
        onMenuClick={onMenuClick}
        onFacilityClick={onFacilityClick}
        onNoticeClick={onNoticeClick}
        onBusinessClick={onBusinessClick}
        onHomeClick={onHomeClick}
      />
    </>
  );
}